import { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { LanguageContext } from '../../contexts/LanguageProvider';
import ChapterTextEditor from '../ChapterTextEditor/ChapterTextEditor';
import PaginationComponent from '../PaginationComponent';
import { Card, ListGroup, Button, Form, Badge, Stack, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../configs/apiConfig';
import translations from "./translations";
import useMediaQuery from '../../utils/useMediaQuery';
import { useAlertContext } from '../../contexts/AlertContext';
import BrandSelect from '../common/BrandSelect';
import { FaRegFileAlt } from 'react-icons/fa';

// Função para truncar texto
const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
};

function ChapterEditing() {
  const { isLoggedIn } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const [chapters, setChapters] = useState([]);
  const [editors, setEditors] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [editChapterText, setEditChapterText] = useState(false);
  const [comments, setComments] = useState([]);
  const [allComments, setAllComments] = useState([]);
  const [selectedComments, setSelectedComments] = useState([]);
  const [currentCommentsPage, setCurrentCommentsPage] = useState(1);
  const [commentsPerPage] = useState(5);
  const [totalCommentsPages, setTotalCommentsPages] = useState(1);
  const [totalComments, setTotalComments] = useState(0);
  const [forceFetch, setForceFetch] = useState(false);

  const { showSuccess, showError } = useAlertContext();
  const t = translations[language] || translations.pt;
  const isMobile = useMediaQuery('(max-width: 768px)');

  const indexOfLastComment = currentCommentsPage * commentsPerPage;
  const indexOfFirstComment = indexOfLastComment - commentsPerPage;

  // Função para download de arquivo Base64
  const handleDownload = useCallback((base64String, fileName) => {
    if (!base64String || !fileName) return;

    const link = document.createElement("a");
    let mimeType = "application/octet-stream";
    const parts = fileName.split(".");
    const extension = parts.length > 1 ? parts.pop().toLowerCase() : "";

    switch (extension) {
      case "png": mimeType = "image/png"; break;
      case "jpg":
      case "jpeg": mimeType = "image/jpeg"; break;
      case "gif": mimeType = "image/gif"; break;
      case "bmp": mimeType = "image/bmp"; break;
      case "webp": mimeType = "image/webp"; break;

      case "pdf": mimeType = "application/pdf"; break;
      case "txt": mimeType = "text/plain"; break;
      case "csv": mimeType = "text/csv"; break;
      case "json": mimeType = "application/json"; break;
      case "xml": mimeType = "application/xml"; break;
      case "md": mimeType = "text/markdown"; break;

      case "zip": mimeType = "application/zip"; break;
      case "rar": mimeType = "application/vnd.rar"; break;
      case "7z": mimeType = "application/x-7z-compressed"; break;

      default: mimeType = "application/octet-stream"; break;
    }

    link.href = `data:${mimeType};base64,${base64String}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchData = async () => {
      try {
        const auth = localStorage.getItem('auth');
        const chaptersResponse = await axios.get(API_ENDPOINTS.CHAPTERS_OWNED(0), {
          headers: { 'Authorization': `Basic ${auth}` }
        });

        setChapters(chaptersResponse.data);

        const editorsResponse = await axios.get(API_ENDPOINTS.USERS_EDITORS, {
          headers: { 'Authorization': `Basic ${auth}` }
        });
        setEditors(editorsResponse.data);
      } catch (error) {
        showError(`${t.failedToLoadData}: ${error.message}`);
      }
    };

    fetchData();
  }, [isLoggedIn, t.failedToLoadData, showError, forceFetch]);

  useEffect(() => {
    if (!isLoggedIn || !selectedChapter) return;

    const fetchChapterData = async () => {
      try {
        const auth = localStorage.getItem('auth');
        const commentsResponse = await axios.get(
          API_ENDPOINTS.COMMENTS_BY_CHAPTER(selectedChapter.id),
          { headers: { 'Authorization': `Basic ${auth}` } }
        );
        const sortedComments = commentsResponse.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setAllComments(sortedComments);
      } catch (error) {
        showError(`${t.failedToLoadChapterData}: ${error.message}`);
      }
    };

    fetchChapterData();
  }, [isLoggedIn, selectedChapter, t.failedToLoadChapterData, showError]);

  useEffect(() => {
    const startIndex = (currentCommentsPage - 1) * commentsPerPage;
    const endIndex = startIndex + commentsPerPage;

    setComments(allComments.slice(startIndex, endIndex));
    setTotalComments(allComments.length);
    setTotalCommentsPages(Math.ceil(allComments.length / commentsPerPage));
  }, [allComments, currentCommentsPage, commentsPerPage]);

  const handleCommentsPageChange = (pageNumber) => {
    setCurrentCommentsPage(pageNumber);
  };

  const handleUpdateChapter = async (chapterId, updatedFields) => {
    try {
      const auth = localStorage.getItem('auth');
      const fieldsToUpdate = { ...updatedFields };
      delete fieldsToUpdate.content;

      await axios.put(
        API_ENDPOINTS.CHAPTERS_BY_ID(chapterId),
        fieldsToUpdate,
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setChapters(chapters.map(ch =>
        ch.id === chapterId ? { ...ch, ...fieldsToUpdate } : ch
      ));

      if (selectedChapter && selectedChapter.id === chapterId) {
        setSelectedChapter({ ...selectedChapter, ...fieldsToUpdate });
      }

      showSuccess(t.chapterUpdatedSuccessfully);
    } catch (error) {
      showError(`${t.failedToUpdateChapter}: ${error.message}`);
    }
  };

  const handleCommentAction = async (commentId, newState) => {
    try {
      const auth = localStorage.getItem('auth');
      await axios.put(
        API_ENDPOINTS.COMMENTS_BY_ID_STATE(commentId),
        newState,
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setAllComments(allComments.filter(c => c.id !== commentId));
      setComments(comments.filter(c => c.id !== commentId));
      setSelectedComments(selectedComments.filter(id => id !== commentId));
      showSuccess(newState === 'DELETED_CHAPTER_OWNER' ? t.commentDeleted : t.commentProcessed);
    } catch (error) {
      showError(`${t.failedToUpdateComment}: ${error.message}`);
    }
  };

  const handleChapter = async (chapterID) => {
    const auth = localStorage.getItem('auth');
    const chapter = await axios.get(
      API_ENDPOINTS.CHAPTERS_BY_ID(chapterID),
      { headers: { 'Authorization': `Basic ${auth}` } }
    );
    setSelectedChapter(chapter.data);
    setEditChapterText(true);
  };

  const handleChapterEditChange = (chapterId, field, value) => {
    setChapters(chapters.map(chapter =>
        chapter.id === chapterId ? { ...chapter, [field]: value } : chapter
      ));
    if (selectedChapter && selectedChapter.id === chapterId) {
      setSelectedChapter({ ...selectedChapter, [field]: value });
    }
  };

  const handleChapterTextBack = (updatedChapter) => {
    setSelectedChapter(updatedChapter);
    setEditChapterText(false);
  };

  return (
    <div className="page-section">
      <Card>
        <Card.Header as="h2" className="border-bottom">
          <Stack direction="horizontal" gap={3}>
            <div className="d-inline-flex align-items-center gap-2"><FaRegFileAlt className="text-primary" /> {t.editChapters}</div>
            {selectedChapter && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setSelectedChapter(null);
                  setEditChapterText(false);
                  setForceFetch(!forceFetch);
                }}
                className="ms-auto"
              >
                {t.backToChapterSelection}
              </Button>
            )}
          </Stack>
        </Card.Header>

        <Card.Body className='p-3'>
          {!selectedChapter ? (
            <div className="chapter-list">
              <h5 className="mb-3">{t.selectAChapter}</h5>
              <ListGroup>
                {chapters
                  .sort((a, b) => a.position - b.position)
                  .map(chapter => {
                    const chapterTitle = isMobile
                      ? truncateText(chapter.title, 30)
                      : chapter.title;

                    const bookTitle = isMobile
                      ? truncateText(chapter.book.title, 20)
                      : chapter.book.title;

                    return (
                      <ListGroup.Item
                        key={chapter.id}
                        action
                        onClick={() => setSelectedChapter(chapter)}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>{chapterTitle}</span>
                        <div className="ms-auto" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-book-title-${chapter.id}`}>
                                {chapter.book.title}
                              </Tooltip>
                            }
                          >
                            <Badge bg="secondary">
                              {bookTitle}
                            </Badge>
                          </OverlayTrigger>
                          <Badge bg={chapter.visible ? 'success' : 'danger'}>
                            {chapter.visible ? t.visible : t.hidden}
                          </Badge>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
              </ListGroup>
            </div>
          ) : (
            <div className="chapter-editor">
              {!editChapterText && (
                <>
                  <div className="editor-header mb-4">
                    <h4>{selectedChapter.title}</h4>
                    <p className="text-muted">{t.book}: {selectedChapter.book.title}</p>

                    <Stack direction="horizontal" gap={2} className="mb-3">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleUpdateChapter(selectedChapter.id, {
                          title: selectedChapter.title,
                          owner: selectedChapter.owner,
                          context: selectedChapter.context,
                          tags: selectedChapter.tags || null
                        })}
                      >
                        {t.save}
                      </Button>
                      <Button
                        variant={selectedChapter.visible ? 'secondary' : 'success'}
                        size="sm"
                        onClick={() => handleUpdateChapter(selectedChapter.id, {
                          owner: selectedChapter.owner,
                          title: selectedChapter.title,
                          context: selectedChapter.context,
                          visible: !selectedChapter.visible,
                          tags: selectedChapter.tags || null
                        })}
                      >
                        {selectedChapter.visible ? t.hideChapter : t.publishChapter}
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleChapter(selectedChapter.id)}
                      >
                        {t.editText}
                      </Button>
                    </Stack>
                  </div>

                  <div className="border p-3 rounded mb-3">
                    <h5>{t.properties}</h5>
                    <Form.Group className="mb-3">
                      <Form.Label>{t.title}</Form.Label>
                      <Form.Control
                        value={selectedChapter.title}
                        onChange={(e) => handleChapterEditChange(selectedChapter.id, 'title', e.target.value)}
                      />
                    </Form.Group >
                    <Form.Group className="mb-3">
                      <Form.Label>{t.context}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={selectedChapter.context}
                        placeholder={t.contextPlanceholder}
                        onChange={(e) => handleChapterEditChange(selectedChapter.id, 'context', e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>{t.responsible}</Form.Label>
                      <BrandSelect
                        value={selectedChapter.owner?.id || ''}
                        onChange={(v) => handleChapterEditChange(selectedChapter.id, 'owner', { id: v })}
                        disabled
                        options={editors.map(editor => ({ value: editor.id, label: editor.name }))}
                      />
                    </Form.Group>
                  </div>

                  <div className="mt-4 border-top pt-3">
                    <h5>{t.suggestions} ({totalComments})</h5>
                    <ListGroup variant="flush">
                      {comments.map(comment => (
                        <ListGroup.Item key={comment.id} className="d-flex align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between">
                              <div>
                                <strong>{comment.creator.name}</strong>
                                <span className="text-muted ms-2">
                                  {new Date(comment.createdAt).toLocaleString()}
                                </span>
                              </div>
                              {comment.state === 'BOOK_OWNER' && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() => handleCommentAction(comment.id, 'DELETED_CHAPTER_OWNER')}
                                >
                                  {t.delete}
                                </Button>
                              )}
                            </div>
                            <div>{comment.text}</div>
                            <div className="d-flex gap-2 mt-1">
                              {comment.pageNumber && (
                                <Badge bg="secondary" className="align-content-center">
                                  {t.pageNumber}{comment.pageNumber}
                                </Badge>
                              )}
                              {comment.sugestionType && (
                                <Badge bg="info" className="align-content-center" pill="true">
                                  {comment.sugestionType}
                                </Badge>
                              )}
                              {comment.imageBase64 && comment.nameImageBase64 && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleDownload(comment.imageBase64, comment.nameImageBase64)}
                                >
                                  Download: {comment.nameImageBase64}
                                </Button>
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>

                    <PaginationComponent
                      totalPages={totalCommentsPages}
                      currentPage={currentCommentsPage}
                      setCurrentPage={handleCommentsPageChange}
                      lengthTotal={totalComments}
                      tItemName={t.commentsLow}
                      t={t}
                      indexOfFirst={indexOfFirstComment}
                      indexOfLast={indexOfLastComment}
                      isMobile={isMobile}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {selectedChapter && editChapterText && (
            <ChapterTextEditor entity={selectedChapter} onBack={handleChapterTextBack} />
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default ChapterEditing;